import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { CSSNeuralPoster } from './CSSNeuralPoster';

function isWebGLAvailable(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.location.search.includes('nowebgl') || window.location.hash.includes('nowebgl')) {
    return false;
  }
  try {
    const canvas = document.createElement('canvas');
    return !!(
      window.WebGLRenderingContext &&
      (canvas.getContext('webgl2') ||
        canvas.getContext('webgl') ||
        canvas.getContext('experimental-webgl'))
    );
  } catch {
    return false;
  }
}

function createParticleTexture(): THREE.Texture {
  const canvas = document.createElement('canvas');
  canvas.width = 32;
  canvas.height = 32;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const grad = ctx.createRadialGradient(16, 16, 0, 16, 16, 16);
    grad.addColorStop(0, 'rgba(255, 255, 255, 1)');
    grad.addColorStop(0.35, 'rgba(255, 255, 255, 0.85)');
    grad.addColorStop(0.7, 'rgba(255, 255, 255, 0.25)');
    grad.addColorStop(1, 'rgba(255, 255, 255, 0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 32, 32);
  }
  const texture = new THREE.CanvasTexture(canvas);
  return texture;
}

export const NeuralCore3D: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [webglReady, setWebglReady] = useState<boolean>(() => isWebGLAvailable());

  useEffect(() => {
    if (!isWebGLAvailable()) {
      setWebglReady(false);
      return;
    }

    const container = containerRef.current;
    if (!container) return;

    let renderer: THREE.WebGLRenderer | null = null;
    let canvas: HTMLCanvasElement | null = null;
    let scene: THREE.Scene | null = null;
    let camera: THREE.PerspectiveCamera | null = null;
    let geometry: THREE.BufferGeometry | null = null;
    let material: THREE.PointsMaterial | null = null;
    let particleTexture: THREE.Texture | null = null;
    let particleSystem: THREE.Points | null = null;
    let animId = 0;
    let onContextLost: ((e: Event) => void) | null = null;
    let onContextRestored: (() => void) | null = null;

    try {
      // 1. Initial Dimensions (Viewport full-screen backdrop)
      const width = window.innerWidth;
      const height = window.innerHeight;
      const isMobile = width < 768;

      scene = new THREE.Scene();
      camera = new THREE.PerspectiveCamera(55, width / height, 0.1, 1000);
      camera.position.z = isMobile ? 24 : 18;

      // 2. High-Stability WebGL Renderer Configuration
      // preserveDrawingBuffer: true guarantees the Android Chrome compositor thread
      // never composites an uninitialized/cleared buffer between rAF frames during scroll gestures!
      renderer = new THREE.WebGLRenderer({
        antialias: false,
        alpha: true,
        powerPreference: 'high-performance',
        depth: false,
        stencil: false,
        precision: 'mediump',
        preserveDrawingBuffer: true,
      });
      renderer.setClearColor(0x070709, 1.0);
      renderer.setSize(width, height);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.35));

      canvas = renderer.domElement;
      canvas.style.display = 'block';
      canvas.style.position = 'absolute';
      canvas.style.top = '0';
      canvas.style.left = '0';
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      canvas.style.backgroundColor = '#070709';
      canvas.style.pointerEvents = 'none';
      canvas.style.transform = 'translate3d(0, 0, 0)';
      (canvas.style as any).webkitTransform = 'translate3d(0, 0, 0)';
      canvas.style.backfaceVisibility = 'hidden';
      (canvas.style as any).webkitBackfaceVisibility = 'hidden';
      container.appendChild(canvas);

      // Context loss handlers
      onContextLost = (e: Event) => {
        e.preventDefault();
        console.warn('[NeuralCore3D] WebGL context lost.');
      };
      onContextRestored = () => {
        console.info('[NeuralCore3D] WebGL context restored.');
      };
      canvas.addEventListener('webglcontextlost', onContextLost, false);
      canvas.addEventListener('webglcontextrestored', onContextRestored, false);

      // 3. Double-Torus Particle Geometry (60-120 FPS calibrated)
      const particleCount = isMobile ? 4200 : 6500;
      geometry = new THREE.BufferGeometry();
      const positions = new Float32Array(particleCount * 3);
      const colors = new Float32Array(particleCount * 3);

      const colorA = new THREE.Color('#FFAF01');
      const colorB = new THREE.Color('#D4AF37');
      const colorC = new THREE.Color('#FA500F');
      const colorD = new THREE.Color('#F55036');

      for (let i = 0; i < particleCount; i++) {
        const u = Math.random() * Math.PI * 2;
        const v = Math.random() * Math.PI * 2;
        const tubeRadius = 2.4 + Math.sin(u * 3) * 0.8;
        const torusRadius = 6.5;

        const x = (torusRadius + tubeRadius * Math.cos(v)) * Math.cos(u);
        const y = (torusRadius + tubeRadius * Math.cos(v)) * Math.sin(u);
        const z = tubeRadius * Math.sin(v) + Math.cos(u * 2) * 1.5;

        const noise = (Math.random() - 0.5) * 0.45;
        positions[i * 3] = x + noise;
        positions[i * 3 + 1] = y + noise;
        positions[i * 3 + 2] = z + noise;

        const mixRatio = Math.sin(u) * 0.5 + 0.5;
        const finalColor = new THREE.Color();
        if (mixRatio < 0.33) {
          finalColor.lerpColors(colorA, colorB, mixRatio * 3);
        } else if (mixRatio < 0.66) {
          finalColor.lerpColors(colorB, colorC, (mixRatio - 0.33) * 3);
        } else {
          finalColor.lerpColors(colorC, colorD, (mixRatio - 0.66) * 3);
        }

        colors[i * 3] = finalColor.r;
        colors[i * 3 + 1] = finalColor.g;
        colors[i * 3 + 2] = finalColor.b;
      }

      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

      particleTexture = createParticleTexture();
      material = new THREE.PointsMaterial({
        size: isMobile ? 0.22 : 0.18,
        map: particleTexture,
        vertexColors: true,
        transparent: true,
        opacity: 0.85,
        blending: THREE.NormalBlending, // NormalBlending prevents additive blowout/whiteout
        depthWrite: false,
      });

      particleSystem = new THREE.Points(geometry, material);
      scene.add(particleSystem);
    } catch (err) {
      console.warn('[NeuralCore3D] WebGL context failed or disabled, falling back to CSS manifold:', err);
      setWebglReady(false);
      return;
    }

    // 4. Mouse Position Lerping (Passive)
    let targetMouseX = 0;
    let targetMouseY = 0;
    let mouseX = 0;
    let mouseY = 0;

    const onPointerMove = (e: MouseEvent) => {
      targetMouseX = (e.clientX / window.innerWidth) * 2 - 1;
      targetMouseY = -(e.clientY / window.innerHeight) * 2 + 1;
    };
    window.addEventListener('mousemove', onPointerMove, { passive: true });

    // 5. Stable Window Resize Handling (Only on genuine orientation or desktop width changes)
    let lastWidth = window.innerWidth;
    const onResize = () => {
      const currentWidth = window.innerWidth;
      const currentHeight = window.innerHeight;
      // Crucial: Only re-allocate renderer buffer if width changed by >50px or orientation changed!
      // This completely ignores mobile Chrome address bar expand/collapse height changes.
      if (Math.abs(currentWidth - lastWidth) > 50) {
        lastWidth = currentWidth;
        if (camera && renderer) {
          const isMob = currentWidth < 768;
          camera.aspect = currentWidth / currentHeight;
          camera.position.z = isMob ? 24 : 18;
          camera.updateProjectionMatrix();
          renderer.setSize(currentWidth, currentHeight);
        }
      }
    };
    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('orientationchange', onResize, { passive: true });

    // 6. Tab Visibility Handling
    let isVisible = true;
    const onVisibilityChange = () => {
      isVisible = !document.hidden;
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    // 7. Continuous Buttery Smooth Render Loop (60-120 FPS, persistent across whole page scroll)
    const clock = new THREE.Clock();

    const animate = () => {
      animId = requestAnimationFrame(animate);

      if (!isVisible || !particleSystem || !renderer || !scene || !camera) {
        return;
      }

      const elapsedTime = clock.getElapsedTime();

      // Smooth mouse lerp
      mouseX += (targetMouseX - mouseX) * 0.05;
      mouseY += (targetMouseY - mouseY) * 0.05;

      // Base rotation + subtle mouse tilt
      particleSystem.rotation.x = elapsedTime * 0.08 + mouseY * 0.35;
      particleSystem.rotation.y = elapsedTime * 0.12 + mouseX * 0.35;

      // Continuous organic breathing pulse
      const breathe = Math.sin(elapsedTime * 0.8) * 0.04 + 1.0;
      particleSystem.scale.set(breathe, breathe, breathe);

      try {
        renderer.render(scene, camera);
      } catch {
        // Suppress transient render errors
      }
    };

    animate();

    // 8. Cleanup on Unmount
    return () => {
      window.removeEventListener('mousemove', onPointerMove);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (animId) cancelAnimationFrame(animId);

      if (geometry) geometry.dispose();
      if (material) material.dispose();
      if (particleTexture) particleTexture.dispose();
      if (renderer) {
        if (canvas && onContextLost && onContextRestored) {
          canvas.removeEventListener('webglcontextlost', onContextLost);
          canvas.removeEventListener('webglcontextrestored', onContextRestored);
        }
        renderer.dispose();
        if (container && renderer.domElement && container.contains(renderer.domElement)) {
          container.removeChild(renderer.domElement);
        }
      }
    };
  }, []);

  if (!webglReady) {
    return <CSSNeuralPoster />;
  }

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 pointer-events-none z-0 overflow-hidden select-none"
      style={{
        backgroundColor: '#070709',
        transform: 'translate3d(0, 0, 0)',
        WebkitTransform: 'translate3d(0, 0, 0)',
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden',
        contain: 'strict',
      }}
      aria-hidden="true"
    />
  );
};
